// Finance & Books: the rental business's own double-entry accounting records.
//
// Everything on this page comes from posted journals. Drafts and the legacy
// Administration archive are shown separately and never mixed into the
// numbers. Wording stays "Internal Accounting Records / Accountant-Ready
// Records": ConfirmPH does not register books with any agency.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpenCheck, Coins, FileText, Landmark, ListChecks, PiggyBank, Receipt, Scale, Settings2,
         Wallet } from "lucide-react";

import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CAPABILITIES, RECORDS_LABEL, can } from "../lib/books";
import { BooksSettings, EnableBooks } from "../components/finance/BooksSetup";
import BooksOverview from "../components/finance/BooksOverview";
import ChargeDraftsTab from "../components/finance/ChargeDraftsTab";
import MoneyTab from "../components/finance/MoneyTab";
import ExpensesTab from "../components/finance/ExpensesTab";
import LegacyTab from "../components/finance/LegacyTab";
import { DepositsTab, JournalsTab, PeriodsTab, ReportsTab } from "../components/finance/LedgerTabs";
import { Empty, Loading, Section, Table } from "../components/finance/shared";

const TABS = [
  { key: "overview", label: "Overview", icon: Scale },
  { key: "charges", label: "Rental charges", icon: Receipt },
  { key: "money", label: "Money in", icon: Coins },
  { key: "expenses", label: "Money out", icon: Wallet },
  { key: "deposits", label: "Deposits", icon: PiggyBank },
  { key: "journals", label: "Journals", icon: FileText },
  { key: "reports", label: "Reports", icon: ListChecks },
  { key: "periods", label: "Monthly close", icon: Landmark },
  { key: "setup", label: "Setup & history", icon: Settings2 },
];

export default function FinanceBooks() {
  const { user } = useAuth();
  const isOwner = (user?.account_type || user?.role) === "business_owner";
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [refreshKey, setRefreshKey] = useState(0);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/status");
      setStatus(data);
      if (data.books_enabled && (data.capabilities || []).includes(CAPABILITIES.view)) {
        const accountsResponse = await api.get("/finance/accounts");
        setAccounts(accountsResponse.data || []);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus, refreshKey]);

  const changed = () => setRefreshKey((n) => n + 1);

  if (loading) return <Loading label="Opening your books..." />;

  const hasView = can(status, CAPABILITIES.view);

  if (!status?.books_enabled) {
    return (
      <div className="space-y-4" data-testid="finance-books-page">
        <Header enabled={false} />
        {hasView || isOwner
          ? <EnableBooks status={status} onEnabled={changed} />
          : <Section><Empty>Finance & Books has not been switched on for this business.</Empty></Section>}
      </div>
    );
  }

  if (!hasView) {
    return (
      <div className="space-y-4" data-testid="finance-books-page">
        <Header enabled />
        <Section>
          <Empty testId="books-no-access">
            You don't have access to Finance & Books. Ask the owner to grant the finance permissions you need.
          </Empty>
        </Section>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="finance-books-page">
      <Header enabled profile={status.profile} />

      <div className="overflow-x-auto">
        <div className="flex gap-1.5 min-w-max" role="tablist" data-testid="books-tabs">
          {TABS.map((item) => {
            const Icon = item.icon;
            const active = tab === item.key;
            return (
              <button key={item.key} role="tab" aria-selected={active} onClick={() => setTab(item.key)}
                      data-testid={`books-tab-${item.key}`}
                      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium border
                                  ${active ? "border-slate-900 bg-slate-900 text-white"
                                           : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
                <Icon className="w-4 h-4" /> {item.label}
              </button>
            );
          })}
        </div>
      </div>

      <div key={`${tab}-${refreshKey}`}>
        {tab === "overview" && <BooksOverview onOpenTab={setTab} />}
        {tab === "charges" && <ChargeDraftsTab status={status} onChanged={changed} />}
        {tab === "money" && <MoneyTab status={status} accounts={accounts} onChanged={changed} />}
        {tab === "expenses" && <ExpensesTab status={status} accounts={accounts} onChanged={changed} />}
        {tab === "deposits" && <DepositsTab />}
        {tab === "journals" && <JournalsTab status={status} onChanged={changed} />}
        {tab === "reports" && <ReportsTab status={status} />}
        {tab === "periods" && <PeriodsTab status={status} isOwner={isOwner} onChanged={changed} />}
        {tab === "setup" && (
          <div className="space-y-4">
            <BooksSettings status={status} accounts={accounts} onSaved={changed} />
            <ChartOfAccounts accounts={accounts} />
            <LegacyTab status={status} accounts={accounts} onChanged={changed} />
          </div>
        )}
      </div>
    </div>
  );
}

function Header({ enabled, profile }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-1">
          <BookOpenCheck className="w-3.5 h-3.5" /> {RECORDS_LABEL}
        </div>
        <h1 className="font-display text-3xl font-bold mt-1">Finance &amp; Books</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          {enabled
            ? "Double-entry records for this business: what you earned, what you hold for customers, what you owe " +
              "and what you spent - ready for your accountant."
            : "Turn on double-entry accounting records for this business."}
          {profile?.go_live_date && <span className="block text-xs text-slate-500 mt-1">
            Books started {profile.go_live_date}. Timezone {profile.timezone}. Currency {profile.currency}.
          </span>}
        </p>
      </div>
    </div>
  );
}

function ChartOfAccounts({ accounts }) {
  const [open, setOpen] = useState(false);
  return (
    <Section title="Chart of accounts"
             description="The accounts your entries are recorded against."
             testId="books-chart"
             actions={<button onClick={() => setOpen(!open)} className="text-sm text-slate-600 hover:text-slate-900">
                        {open ? "Hide" : `Show ${accounts.length} accounts`}
                      </button>}>
      {open && (
        <Table head={["Code", "Account", "Type", "Used by"]}>
          {accounts.map((account) => (
            <tr key={account.id} data-testid={`books-account-${account.code}`}>
              <td className="py-2 pr-3 text-slate-500">{account.code}</td>
              <td className="py-2 pr-3">{account.name}</td>
              <td className="py-2 pr-3 text-slate-600">{account.account_type.replace("_", " ")}</td>
              <td className="py-2 pr-3 text-xs text-slate-500">
                {account.allow_manual ? "Manual entries allowed" : "Workflow only (controlled)"}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Section>
  );
}
